'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import sanitizeHtml from 'sanitize-html';
import { AnimatePresence, motion } from 'framer-motion';
import { Product } from '../hiv-test/types';
import AddToCartButton from './AddToCartButton';

const sanitizeAndRenderHtml = (
  content: string | undefined
): { __html: string } | undefined => {
  if (!content) return undefined;

  const sanitized = sanitizeHtml(content, {
    allowedAttributes: {
      span: ['class'],
    },
    allowedClasses: {
      span: ['*'], // Allow all classes on span elements
    },
    allowedTags: ['b', 'strong', 'i', 'em', 'u', 'span', 'br', 'p'],
  });

  return { __html: sanitized };
};

interface ProductDetailsProps {
  product?: Product | null;
  heading?: string;
  buttonText?: string;
  kitContentsHeading?: string;
  kitContents?: Array<{
    item: string;
    subItems?: Array<{
      label?: string;
      items: Array<{ item: string }>;
    }>;
  }>;
  faqLinkText?: string;
  faqHref?: string;
}

const ProductDetails = ({
  buttonText = 'Purchase A Kit',
  faqHref = '/#faq',
  faqLinkText = 'FAQ',
  heading = "What's Inside",
  kitContents = [
    {
      item: '1x INSTI® HIV Self Test',
      subItems: [
        {
          items: [
            { item: '1x Sterile Lancet' },
            { item: '1x Finger Bandage' },
            { item: '3x Liquid Solutions' },
            { item: '1x Test Device' },
            { item: 'Instruction Sheet' },
          ],
          label: 'Includes:',
        },
      ],
    },
    { item: '1x Pipette' },
    {
      item: 'Safe Sex Materials:',
      subItems: [
        {
          items: [{ item: '2x Condoms' }, { item: '1x Lubricant' }],
        },
      ],
    },
  ],
  kitContentsHeading = "What's inside?",
  product,
}: ProductDetailsProps) => {
  const [isProductDescriptionOpen, setIsProductDescriptionOpen] =
    useState<boolean>(false);

  return (
    <section className="flex w-full flex-col items-center bg-white py-[48px] md:py-[24px]">
      <div className="content-wrapper flex w-full flex-col border-b pb-12">
        <Image
          className="relative top-4 h-auto min-h-[520px] w-full rounded-t-[10px] object-cover object-center"
          height={300}
          width={1200}
          alt="trusti HIV test kit unboxing"
          src="/trusti-kit-unboxing.png"
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 1200px"
          placeholder="blur"
          blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
          priority
        />
        <div className="z-10 flex flex-col justify-between rounded-[10px] bg-[#8875D5] p-[24px_20px]">
          <div className="flex w-full justify-between">
            <h4
              className="align-middle font-['Work_Sans'] text-[44px] leading-[60px] font-normal tracking-[-5%] text-white sm:max-w-[240px] sm:text-[32px] sm:leading-[45px]"
              dangerouslySetInnerHTML={sanitizeAndRenderHtml(heading)}
            />
            <button
              onClick={() =>
                setIsProductDescriptionOpen(!isProductDescriptionOpen)
              }
            >
              <Image
                height={60}
                width={60}
                alt={
                  isProductDescriptionOpen
                    ? 'Hide kit contents'
                    : 'Show kit contents'
                }
                src={
                  isProductDescriptionOpen
                    ? '/orange-gradient-subtract-icon.svg'
                    : '/gradient-plus-icon.svg'
                }
                sizes="60px"
                placeholder="blur"
                blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCdABmX/9k="
              />
            </button>
          </div>
        </div>
        <AnimatePresence>
          {isProductDescriptionOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                duration: 0.3,
                ease: 'easeInOut',
                height: { duration: 0.4 },
              }}
              style={{ overflow: 'hidden' }}
            >
              <div className="relative -bottom-4 z-0 box-border flex min-h-[460px] w-full justify-end rounded-b-[10px] bg-[url('/trusti-kit-contents.png')] bg-cover bg-center">
                <div className="w-full rounded-b-[10px] bg-[#ebece8] p-[40px_24px] font-['Work_Sans'] md:w-[30%] md:rounded-br-[10px]">
                  <p
                    className="mb-6 align-middle text-xl leading-7 font-medium tracking-[0%]"
                    dangerouslySetInnerHTML={sanitizeAndRenderHtml(
                      kitContentsHeading
                    )}
                  />
                  <ul className="list-inside list-disc">
                    {kitContents.map((content, index) => (
                      <li key={index}>
                        <span
                          dangerouslySetInnerHTML={sanitizeAndRenderHtml(
                            content.item
                          )}
                        />
                        {content.subItems && (
                          <ul className="ml-6">
                            {content.subItems.map((subItem, subIndex) => (
                              <li key={subIndex}>
                                <span
                                  dangerouslySetInnerHTML={sanitizeAndRenderHtml(
                                    subItem.label
                                  )}
                                />
                                {subItem.items && (
                                  <ul className="ml-6">
                                    {subItem.items.map((item, itemIndex) => (
                                      <li key={itemIndex}>
                                        <span
                                          dangerouslySetInnerHTML={sanitizeAndRenderHtml(
                                            item.item
                                          )}
                                        />
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4">
                    Got questions? Check out our{' '}
                    <Link href={faqHref} className="underline">
                      {faqLinkText}
                    </Link>
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <AddToCartButton
          product={product}
          buttonText={buttonText}
          className="mt-8 w-full sm:hidden"
        />
      </div>
    </section>
  );
};

export default ProductDetails;
